import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../components/appIcon';
import Button from '../../components/ui/Button';

// Modules
import IncomeStatement from './components/IncomeStatement';
import BalanceSheet from './components/BalanceSheet';
import CashFlow from './components/CashFlow';
import ComparativeReports from './components/ComparativeReports';
import ExportPanel from './components/ExportPanel';
import ClientReports from './components/ClientReports';

/* =========================================================
   Design tokens (same family as BudgetModal)
========================================================= */
const APP_BG =
  'bg-gradient-to-b from-gray-950 via-gray-950/95 to-gray-900 text-white';
const CARD =
  'bg-gray-900/80 border border-gray-800/90 rounded-2xl shadow-inner shadow-black/40';
const SHELL =
  'bg-gray-950/98 border border-gray-900/80 shadow-[0_0_0_1px_rgba(15,23,42,0.9),0_40px_120px_rgba(0,0,0,0.85)]';
const SOFT_RING =
  'shadow-[0_0_22px_rgba(34,211,238,0.18)] border border-cyan-500/25';

const APP_NAV = [
  { id: 'work-orders', label: 'Órdenes', icon: 'ClipboardList', path: '/work-orders' },
  { id: 'inventory', label: 'Inventario', icon: 'Boxes', path: '/inventory' },
  { id: 'financial', label: 'Finanzas', icon: 'BarChart3', path: '/financial-reports' }
];

const REPORT_MODULES = [
  {
    id: 'income-statement',
    title: 'Estado de Resultados',
    description: 'Ingresos, costos, gastos y utilidad',
    icon: 'TrendingUp'
  },
  {
    id: 'balance-sheet',
    title: 'Balance General',
    description: 'Activos, pasivos y patrimonio',
    icon: 'Scale'
  },
  {
    id: 'cash-flow',
    title: 'Flujo de Efectivo',
    description: 'Operación, inversión y financiamiento',
    icon: 'RefreshCw'
  },
  {
    id: 'comparative',
    title: 'Comparativos',
    description: 'Tendencias por período',
    icon: 'Compare'
  },
  {
    id: 'clients',
    title: 'Clientes',
    description: 'Rentabilidad por cliente',
    icon: 'Users'
  },
  {
    id: 'export',
    title: 'Exportaciones',
    description: 'PDF / Excel / CSV',
    icon: 'Download'
  }
];

const cx = (...cls) => cls.filter(Boolean).join(' ');

/* =========================================================
   Global App Navbar (for whole app)
========================================================= */
function GlobalTopNav({ onNavigate, activePath, onOpenMobile }) {
  return (
    <div className="sticky top-0 z-40">
      <div className="bg-black/60 backdrop-blur-xl border-b border-gray-800/80">
        <div className="px-4 lg:px-8 py-3 flex items-center justify-between gap-3">
          {/* Brand */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-cyan-500 via-blue-500 to-indigo-500 flex items-center justify-center shadow-xl shadow-cyan-500/25">
              <Icon name="BarChart3" size={20} className="text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-white font-bold leading-tight truncate">Sistema</p>
              <p className="text-[11px] text-cyan-200/80 truncate">
                Órdenes • Inventario • Finanzas
              </p>
            </div>
          </div>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-2">
            {APP_NAV.map((m) => {
              const isActive = activePath === m.path;
              return (
                <button
                  key={m.id}
                  onClick={() => onNavigate(m.path)}
                  className={cx(
                    'px-4 py-2 rounded-xl text-sm font-semibold transition-all flex items-center gap-2 border',
                    isActive
                      ? 'bg-cyan-500/10 border-cyan-500/35 text-cyan-200 shadow-[0_0_18px_rgba(34,211,238,0.18)]'
                      : 'bg-gray-950/40 border-gray-800 text-gray-300 hover:text-white hover:bg-gray-900/60 hover:border-cyan-500/25'
                  )}
                >
                  <Icon name={m.icon} size={16} />
                  {m.label}
                </button>
              );
            })}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Mobile menu */}
            <button
              onClick={onOpenMobile}
              className="lg:hidden p-2 rounded-xl border border-gray-800 bg-gray-950/40 hover:bg-gray-900/60"
              aria-label="Abrir menú"
            >
              <Icon name="Menu" size={18} className="text-gray-200" />
            </button>

            <div className="hidden sm:flex items-center gap-2">
              <div className="px-3 py-2 rounded-xl bg-gray-950/40 border border-gray-800 text-xs text-gray-300 flex items-center gap-2">
                <Icon name="Shield" size={14} className="text-cyan-300" />
                Sistema seguro
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   Mobile App Drawer
========================================================= */
function MobileAppMenu({ open, onClose, activePath, onNavigate }) {
  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50">
          <motion.div
            className="absolute inset-0 bg-black/80 backdrop-blur-xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            initial={{ x: -24, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -24, opacity: 0 }}
            transition={{ type: 'spring', damping: 24, stiffness: 260 }}
            className="absolute left-0 top-0 bottom-0 w-[86%] max-w-sm bg-gray-950/98 border-r border-gray-800/90 p-4"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-cyan-500 via-blue-500 to-indigo-500 flex items-center justify-center shadow-lg shadow-cyan-500/20">
                  <Icon name="BarChart3" size={18} className="text-white" />
                </div>
                <div>
                  <p className="text-white font-bold">Navegación</p>
                  <p className="text-[11px] text-gray-400">Ir a módulos</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-xl hover:bg-gray-900"
                aria-label="Cerrar menú"
              >
                <Icon name="X" size={18} className="text-gray-300" />
              </button>
            </div>

            <div className="space-y-2">
              {APP_NAV.map((m) => {
                const isActive = activePath === m.path;
                return (
                  <button
                    key={m.id}
                    onClick={() => {
                      onNavigate(m.path);
                      onClose();
                    }}
                    className={cx(
                      'w-full flex items-center gap-3 p-3 rounded-2xl border text-left transition-all',
                      isActive
                        ? 'bg-cyan-500/10 border-cyan-500/35 text-cyan-200'
                        : 'bg-gray-950/40 border-gray-800 text-gray-300 hover:bg-gray-900/60 hover:border-cyan-500/25'
                    )}
                  >
                    <div
                      className={cx(
                        'w-10 h-10 rounded-xl flex items-center justify-center border',
                        isActive
                          ? 'bg-gradient-to-br from-cyan-500 to-blue-600 border-cyan-500/30'
                          : 'bg-gray-900/60 border-gray-800'
                      )}
                    >
                      <Icon name={m.icon} size={18} className={isActive ? 'text-white' : 'text-gray-300'} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{m.label}</p>
                      <p className="text-[11px] text-gray-400 truncate">{m.path}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-6 p-4 rounded-2xl bg-gray-900/60 border border-gray-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/25 flex items-center justify-center">
                  <Icon name="Info" size={18} className="text-cyan-300" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">Tip</p>
                  <p className="text-[11px] text-gray-400">
                    Mantén tu info organizada por módulos.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

/* =========================================================
   Financial Dashboard
========================================================= */
const FinancialReportingDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeModule, setActiveModule] = useState('income-statement');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [dateRange, setDateRange] = useState(() => {
    const start = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
      .toISOString()
      .split('T')[0];
    const end = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
      .toISOString()
      .split('T')[0];
    return { start, end, label: 'Este Mes' };
  });

  const activeModuleMeta = useMemo(
    () => REPORT_MODULES.find((m) => m.id === activeModule),
    [activeModule]
  );

  const handleExport = (payloadOrFormat) => {
    // aquí luego lo conectamos a tu export real
    console.log('EXPORT =>', payloadOrFormat);
  };

  const handleDateRangeChange = (newRange) => setDateRange(newRange);

  const moduleProps = useMemo(
    () => ({
      dateRange,
      onDateRangeChange: handleDateRangeChange,
      onExport: handleExport
    }),
    [dateRange]
  );

  const renderActiveModule = () => {
    switch (activeModule) {
      case 'income-statement':
        return <IncomeStatement {...moduleProps} />;
      case 'balance-sheet':
        return <BalanceSheet {...moduleProps} />;
      case 'cash-flow':
        return <CashFlow {...moduleProps} />;
      case 'comparative':
        return <ComparativeReports {...moduleProps} />;
      case 'clients':
        return <ClientReports {...moduleProps} />;
      case 'export':
        return <ExportPanel {...moduleProps} />;
      default:
        return <IncomeStatement {...moduleProps} />;
    }
  };

  return (
    <div className={cx('min-h-screen', APP_BG)}>
      {/* ✅ Global App Navbar */}
      <GlobalTopNav
        activePath={location.pathname}
        onNavigate={(path) => navigate(path)}
        onOpenMobile={() => setMobileMenuOpen(true)}
      />

      <MobileAppMenu
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        activePath={location.pathname}
        onNavigate={(path) => navigate(path)}
      />

      {/* Content shell */}
      <div className="px-3 sm:px-4 lg:px-8 py-6">
        <div className={cx('w-full rounded-3xl overflow-hidden', SHELL)}>
          <div className="flex flex-col lg:flex-row min-h-[calc(100vh-120px)]">
            {/* Sidebar (desktop): módulos financieros */}
            <aside className="hidden lg:flex lg:flex-col w-[340px] border-r border-gray-900/80 bg-gradient-to-b from-gray-950 via-gray-900 to-gray-900 p-5">
              <div className="mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 via-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center shadow-xl shadow-cyan-500/25">
                    <Icon name="FileText" size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg leading-tight">Finanzas</p>
                    <p className="text-[11px] text-cyan-200/80">Reportes profesionales</p>
                  </div>
                </div>

                <div className="mt-4 p-3 rounded-2xl bg-gray-950/40 border border-gray-800">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-400">Rango</p>
                    <div className="flex items-center gap-2 text-xs text-cyan-200">
                      <Icon name="Calendar" size={14} />
                      <span className="font-mono">{dateRange.label}</span>
                    </div>
                  </div>
                  <div className="mt-2 text-[11px] text-gray-400 font-mono">
                    {dateRange.start} → {dateRange.end}
                  </div>
                </div>
              </div>

              <nav className="space-y-2">
                {REPORT_MODULES.map((m, idx) => {
                  const active = m.id === activeModule;
                  return (
                    <motion.button
                      key={m.id}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 + idx * 0.03 }}
                      onClick={() => setActiveModule(m.id)}
                      className={cx(
                        'w-full flex items-center gap-4 p-4 rounded-2xl text-left transition-all duration-300 group border',
                        active
                          ? 'bg-cyan-500/10 border-cyan-500/35 ' + SOFT_RING
                          : 'bg-gray-900/40 border-transparent hover:bg-gray-800/70 hover:border-cyan-500/25'
                      )}
                    >
                      <div
                        className={cx(
                          'w-12 h-12 rounded-2xl flex items-center justify-center transition-all border',
                          active
                            ? 'bg-gradient-to-br from-cyan-500 to-blue-600 border-cyan-500/35 shadow-lg'
                            : 'bg-gray-800 border-gray-700 group-hover:bg-gray-700'
                        )}
                      >
                        <Icon name={m.icon} size={18} className={active ? 'text-white' : 'text-gray-300'} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={cx('font-semibold text-sm truncate', active ? 'text-cyan-200' : 'text-white')}>
                            {m.title}
                          </span>
                          {active && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="w-2 h-2 bg-cyan-400 rounded-full"
                            />
                          )}
                        </div>
                        <p className="text-[11px] text-gray-400 mt-1 truncate">{m.description}</p>
                      </div>
                    </motion.button>
                  );
                })}
              </nav>

              <div className="mt-auto pt-4">
                <div className="p-4 bg-gray-900/70 rounded-2xl border border-gray-800">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-cyan-500/10 border border-cyan-500/25 flex items-center justify-center">
                      <Icon name="Shield" size={18} className="text-cyan-300" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">Control financiero</p>
                      <p className="text-[11px] text-gray-400">Cálculos consistentes y auditables</p>
                    </div>
                  </div>
                </div>
              </div>
            </aside>

            {/* Main */}
            <main className="flex-1 min-w-0 flex flex-col">
              {/* Module Header */}
              <div className="px-4 sm:px-6 py-4 border-b border-gray-900/80 bg-gradient-to-r from-gray-950 via-gray-900 to-gray-900">
                <div className="flex items-start sm:items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-2xl bg-cyan-500/10 border border-cyan-500/25 flex items-center justify-center">
                        <Icon name={activeModuleMeta?.icon || 'BarChart3'} size={18} className="text-cyan-300" />
                      </div>
                      <div className="min-w-0">
                        <h2 className="text-lg sm:text-xl font-bold text-white truncate">
                          {activeModuleMeta?.title || 'Finanzas'}
                        </h2>
                        <p className="text-[11px] sm:text-xs text-gray-400 truncate">
                          {activeModuleMeta?.description || 'Panel financiero'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Calendar"
                      className="border-gray-800 text-gray-300 hover:bg-gray-900 hover:text-white"
                    >
                      {dateRange.label}
                    </Button>

                    <Button
                      variant="default"
                      size="sm"
                      iconName="Download"
                      onClick={() => handleExport({ reportType: activeModule, range: dateRange })}
                      className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white shadow-lg shadow-cyan-500/25"
                    >
                      Exportar
                    </Button>

                    {/* Mobile: module selector quick */}
                    <div className="lg:hidden">
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="LayoutGrid"
                        onClick={() => {
                          // abre “selector” sencillo usando el mismo menu móvil
                          setMobileMenuOpen(true);
                        }}
                        className="border-gray-800 text-gray-300 hover:bg-gray-900 hover:text-white"
                      >
                        Menú
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Module content */}
              <div className="flex-1 p-3 sm:p-4 lg:p-8 overflow-y-auto">
                {/* Mobile: internal financial modules (cards) */}
                <div className="lg:hidden mb-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {REPORT_MODULES.map((m) => {
                      const active = m.id === activeModule;
                      return (
                        <button
                          key={m.id}
                          onClick={() => setActiveModule(m.id)}
                          className={cx(
                            'p-4 rounded-2xl border text-left transition-all',
                            active
                              ? 'bg-cyan-500/10 border-cyan-500/35 ' + SOFT_RING
                              : 'bg-gray-900/60 border-gray-800 hover:bg-gray-900/80 hover:border-cyan-500/25'
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={cx(
                                'w-11 h-11 rounded-2xl flex items-center justify-center border',
                                active
                                  ? 'bg-gradient-to-br from-cyan-500 to-blue-600 border-cyan-500/35'
                                  : 'bg-gray-950/40 border-gray-800'
                              )}
                            >
                              <Icon name={m.icon} size={18} className={active ? 'text-white' : 'text-gray-300'} />
                            </div>
                            <div className="min-w-0">
                              <p className={cx('font-semibold truncate', active ? 'text-cyan-200' : 'text-white')}>
                                {m.title}
                              </p>
                              <p className="text-[11px] text-gray-400 truncate">{m.description}</p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeModule}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.25 }}
                    className={CARD + ' p-4 sm:p-6'}
                  >
                    {renderActiveModule()}
                  </motion.div>
                </AnimatePresence>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialReportingDashboard;
